/**
 * SOFSEK — Audit Pack export (§41). One-click export for external auditors,
 * scoped by organizational branch and date range: active/expired exceptions
 * with reason codes, mitigating controls, approvers, and exposure windows.
 * A full doc-template-engine binding doesn't fit DOC's single-primary-record
 * model for a multi-section bundle like this — v1 exports the underlying
 * view as CSV, which is what an auditor actually opens anyway.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import type { AuthSession } from '../../types/auth';
import { EmptyState } from '../../components/common/EmptyState';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { useFormatDateTime } from '../../lib/format_date';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { RefreshCw, Search, Download } from 'lucide-react';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';

const PAGE_CODE = 'SFK.AUDIT_PACK.LIST';
const inputCls = 'h-7 rounded border border-csm-border px-2 text-csm-sm';

interface AuditRow {
  violation_id: number;
  rule_id: number;
  scope_org_node_id: number | null;
  violation_status_code: string;
  breach_start_at: string;
  breach_end_at: string | null;
  violation_exception_id: number | null;
  reason_code: string | null;
  approver_id: number | null;
  approval_timestamp: string | null;
  independent_reviewer_id: number | null;
  expiration_date: string | null;
  exception_control_id: number | null;
  mitigating_action_code: string | null;
}

const GRID_COLS: GridColumnDef[] = [
  { key: 'record_id', header: 'Violation', defaultWidth: 100 },
  { key: 'rule', header: 'Rule', defaultWidth: 200 },
  { key: 'status', header: 'Status', defaultWidth: 150 },
  { key: 'breach_start_at', header: 'Breach Start', defaultWidth: 170 },
  { key: 'breach_end_at', header: 'Breach End', defaultWidth: 170 },
  { key: 'reason_code', header: 'Exception Reason', defaultWidth: 160 },
  { key: 'expiration_date', header: 'Expiry', defaultWidth: 130 },
  { key: 'mitigating_action_code', header: 'Mitigating Control', defaultWidth: 180 },
];

interface CodeOpt { code: string; name: string; }

function tlName(list: unknown, fallback: string): string {
  const arr = (list ?? []) as { locale_code: string; display_text: string }[];
  return arr.find(x => x.locale_code === 'EN_US')?.display_text ?? fallback;
}

function AuditPackInner() {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [rules, setRules] = useState<CodeOpt[]>([]);
  const [statuses, setStatuses] = useState<CodeOpt[]>([]);
  const [reasons, setReasons] = useState<CodeOpt[]>([]);
  const [actions, setActions] = useState<CodeOpt[]>([]);
  const [filter, setFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [packRes, ruleRes, statusRes, reasonRes, actionRes] = await Promise.all([
        apiDb.from('sfk_v_audit_pack').select(
          'violation_id, rule_id, scope_org_node_id, violation_status_code, breach_start_at, breach_end_at, violation_exception_id, reason_code, approver_id, approval_timestamp, independent_reviewer_id, expiration_date, exception_control_id, mitigating_action_code'
        ).order('violation_id', { ascending: false }),
        apiDb.from('sfk_conflict_rule').select('rule_id, sfk_conflict_rule_tl ( locale_code, display_text )'),
        apiDb.from('sfk_violation_status').select('violation_status_code, sfk_violation_status_tl ( locale_code, display_text )'),
        apiDb.from('sfk_reason').select('reason_code, sfk_reason_tl ( locale_code, display_text )'),
        apiDb.from('sfk_mitigating_action').select('mitigating_action_code, sfk_mitigating_action_tl ( locale_code, display_text )'),
      ]);
      if (packRes.error) throw packRes.error;
      if (ruleRes.error) throw ruleRes.error;
      if (statusRes.error) throw statusRes.error;
      if (reasonRes.error) throw reasonRes.error;
      if (actionRes.error) throw actionRes.error;
      setRows((packRes.data ?? []) as unknown as AuditRow[]);
      setRules(((ruleRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: String(r.rule_id), name: tlName(r.sfk_conflict_rule_tl, String(r.rule_id)) })));
      setStatuses(((statusRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.violation_status_code as string, name: tlName(r.sfk_violation_status_tl, r.violation_status_code as string) })));
      setReasons(((reasonRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.reason_code as string, name: tlName(r.sfk_reason_tl, r.reason_code as string) })));
      setActions(((actionRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.mitigating_action_code as string, name: tlName(r.sfk_mitigating_action_tl, r.mitigating_action_code as string) })));
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const resolveNameForRule = (id: number) => rules.find(r => r.code === String(id))?.name ?? String(id);
  const resolveNameForStatus = (code: string) => statuses.find(s => s.code === code)?.name ?? code;
  const resolveNameForReason = (code: string | null) => (code ? reasons.find(r => r.code === code)?.name ?? code : '—');
  const resolveNameForAction = (code: string | null) => (code ? actions.find(a => a.code === code)?.name ?? code : '—');

   
  useEffect(() => { void reload(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter(r => {
      const start = new Date(r.breach_start_at).getTime();
      if (dateFrom && start < new Date(dateFrom).getTime()) return false;
      if (dateTo && start > new Date(dateTo).getTime() + 86400000) return false;
      if (q && !resolveNameForRule(r.rule_id).toLowerCase().includes(q) && !resolveNameForStatus(r.violation_status_code).toLowerCase().includes(q)) return false;
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, dateFrom, dateTo, filter, rules, statuses]);

  const detail = useExpandedRows<string>();
  const rowKey = (r: AuditRow) => `${r.violation_id}-${r.violation_exception_id ?? 0}-${r.exception_control_id ?? 0}`;
  // §17.B13 — the exception detail behind each audit-pack row (resolved references).
  const buildRelatedGroups = (r: AuditRow): RelatedChildGroup[] => [
    { key: 'detail', title: t('sfk.audit_pack.related.title', 'Exception detail'), items: [
      { id: 'rule', label: `${t('sfk.audit_pack.col.rule', 'Rule')}: ${resolveNameForRule(r.rule_id)}`, title: String(r.rule_id) },
      { id: 'status', label: `${t('sfk.audit_pack.col.status', 'Status')}: ${resolveNameForStatus(r.violation_status_code)}` },
      ...(r.reason_code ? [{ id: 'reason', label: `${t('sfk.audit_pack.col.reason', 'Exception Reason')}: ${resolveNameForReason(r.reason_code)}` }] : []),
      ...(r.mitigating_action_code ? [{ id: 'action', label: `${t('sfk.audit_pack.col.control', 'Mitigating Control')}: ${resolveNameForAction(r.mitigating_action_code)}` }] : []),
    ] },
  ];

  const exportCsv = () => {
    const headers = ['violation_id', 'rule', 'status', 'breach_start_at', 'breach_end_at', 'exception_reason', 'approver_id', 'approval_timestamp', 'independent_reviewer_id', 'expiration_date', 'mitigating_control'];
    const escape = (v: unknown) => {
      const s = v == null ? '' : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const lines = [
      headers.join(','),
      ...filtered.map(r => [
        r.violation_id, resolveNameForRule(r.rule_id), resolveNameForStatus(r.violation_status_code), r.breach_start_at, r.breach_end_at ?? '',
        r.reason_code ? resolveNameForReason(r.reason_code) : '', r.approver_id ?? '', r.approval_timestamp ?? '', r.independent_reviewer_id ?? '',
        r.expiration_date ?? '', r.mitigating_action_code ? resolveNameForAction(r.mitigating_action_code) : '',
      ].map(escape).join(',')),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sfk_audit_pack_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('sfk.audit_pack.title', 'Audit Pack')}
          description={t('sfk.audit_pack.subtitle', 'Auditor-ready export of exceptions with reasons, controls, approvers and exposure windows.')}
          actions={
            <>
              <button onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}</button>
              <button onClick={exportCsv} className="btn-primary flex items-center gap-1.5"><Download className="h-3.5 w-3.5" /> {t('sfk.audit_pack.export', 'Export Audit Pack')}</button>
            </>
          }
        />
        <StatStrip stats={[{ value: rows.length, label: t('common.total', 'Total') }, { value: filtered.length, label: t('common.shown', 'Shown') }]} />
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <label className="text-csm-xs text-csm-ink-soft">{t('sfk.audit_pack.from', 'From')}</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className={inputCls} />
            <label className="text-csm-xs text-csm-ink-soft">{t('sfk.audit_pack.to', 'To')}</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className={inputCls} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
            <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="border-b border-csm-border px-1 py-1.5 text-left" style={{ width: '1%' }} aria-hidden>&nbsp;</th>
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={rowKey(r)}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle">
                      <RelatedExpandButton open={detail.isOpen(rowKey(r))} hasChildren onToggle={() => detail.toggle(rowKey(r))}
                        expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'record_id' && r.violation_id}
                        {col.key === 'rule' && resolveNameForRule(r.rule_id)}
                        {col.key === 'status' && resolveNameForStatus(r.violation_status_code)}
                        {col.key === 'breach_start_at' && formatDateTime(r.breach_start_at)}
                        {col.key === 'breach_end_at' && (r.breach_end_at ? formatDateTime(r.breach_end_at) : '—')}
                        {col.key === 'reason_code' && resolveNameForReason(r.reason_code)}
                        {col.key === 'expiration_date' && (r.expiration_date ?? '—')}
                        {col.key === 'mitigating_action_code' && resolveNameForAction(r.mitigating_action_code)}
                      </td>
                    ))}
                  </tr>
                  {detail.isOpen(rowKey(r)) && (
                    <tr>
                      <td colSpan={visibleCols.length + 1} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-2">
                        <RelatedChildrenPanel groups={buildRelatedGroups(r)} emptyLabel={t('common.related.none', 'No related records')} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 1} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
            </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>
    </div>
  );
}

export function SfkAuditPackPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <AuditPackInner />
    </PersonalizationProvider>
  );
}
